/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/
import React, { ReactElement, useEffect } from 'react';
import { Loading } from '@carbon/react';
import { useStoreConfig } from '@exo/frontend-features-chrome-logic';
import { ScStoreConfig } from '@exo/frontend-gql-generated-types/graphql';
import { useSessionContext } from '@exo/frontend-common-session-context';
import { useForgotPassword } from '../../hooks/useForgotPassword';

export const ForgotPasswordPageContainer = ({ baseSiteId = 'nestle', render }: Props) => {
  const session = useSessionContext();

  const { getStoreConfig, loading: loadingStoreConfig, data: storeConfig } = useStoreConfig();
  const { sendForgotPasswordEmail } = useForgotPassword();

  useEffect(() => {
    const language = session.language || 'en';
    getStoreConfig({
      variables: { baseSiteId, language, defaultLanguage: 'en' }
    });
  }, [baseSiteId, session.language]);

  if (loadingStoreConfig) {
    return <Loading active={true} withOverlay={true} description="Loading..." />;
  }

  const handleForgotPassword = async (userEmail: string) => {
    const result = await sendForgotPasswordEmail(baseSiteId, userEmail);
    return result.data?.sendForgotPasswordEmail?.wasSuccessful ?? false;
  };

  return render({
    language: session?.language || 'en',
    storeConfig,
    onForgotPassword: handleForgotPassword
  });
};

type Props = {
  baseSiteId: string | undefined;
  render: (args: ForgotPasswordPageContainerRenderProps) => ReactElement | null;
};

export type ForgotPasswordPageContainerRenderProps = {
  language: string;
  storeConfig?: ScStoreConfig;
  onForgotPassword: (userEmail: string) => Promise<boolean>;
};
